import { useRef } from 'react'

/**
 * Starting virtual index for Virtuoso's firstItemIndex.
 * A large number leaves room to prepend older messages by decrementing.
 */
export const FIRST_ITEM_START_INDEX = 100_000

interface UsePrependScrollCompensationParams {
  itemLength: number
  firstItemKey?: string
  conversationId?: string
}

/**
 * Manages Virtuoso's firstItemIndex to prevent scroll-position jumps when older
 * messages are prepended. Computes the index synchronously during render so
 * Virtuoso sees the adjusted value in the same frame as the new items.
 */
export const usePrependScrollCompensation = ({
  itemLength,
  firstItemKey,
  conversationId,
}: UsePrependScrollCompensationParams): number => {
  const totalPrependedRef = useRef(0)
  const prevItemLengthRef = useRef(0)
  const prevFirstKeyRef = useRef<string | undefined>(undefined)
  const prevConversationIdRef = useRef(conversationId)

  if (prevConversationIdRef.current !== conversationId) {
    totalPrependedRef.current = 0
    prevItemLengthRef.current = 0
    prevFirstKeyRef.current = undefined
    prevConversationIdRef.current = conversationId
  }

  const prevLength = prevItemLengthRef.current
  const prevKey = prevFirstKeyRef.current

  const isListGrew = itemLength > prevLength && prevLength > 0
  const isFirstItemChanged = firstItemKey !== prevKey && prevKey !== undefined

  if (isListGrew && isFirstItemChanged) {
    totalPrependedRef.current += itemLength - prevLength
  }

  prevItemLengthRef.current = itemLength
  prevFirstKeyRef.current = firstItemKey

  return FIRST_ITEM_START_INDEX - totalPrependedRef.current
}
